import Box from '@mui/material/Box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import AlertMessages from 'common/Alert/AlertMessages';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import ComponentHeaderTwo from 'common/ComponentHeaderTwo/ComponentHeaderTwo';
import { ReactComponent as Services  } from "assets/SideBarIcons/services.svg";
import StyledInput from "common/SearchField/Search";
import { useAssignServicePriceListService, useGetServicePriceListService, useUnassignServicePriceListService } from 'subModule/src/services/service/useService';
import ServiceTabs from '../ServiceTabs';
import { Grid } from '@mui/material';
import { ServicesPricelistTable } from './ServicesPricelistTable';
import Loader from 'layout/Loader';
import { useForm } from 'react-hook-form';
import { checkPrivileges } from 'Helpers/CheckPrivileges';
import type { TFunction } from 'i18next';
import { selectUserInfo } from 'features/user/userSlice';
import { useSelector } from 'react-redux';
import { ServiceProfileService } from 'subModule/src/core/services';
import { AssignUnassignResponse, GetServiceByIdResponse, ServiceResponse } from 'subModule/src/services/service/interface';
import type { BreadcrumbItem } from 'Helpers/generalBreadcrumbs';
import { pickProfileEntityTitle } from 'Helpers/pickProfileEntityTitle';

const SERVICE_MIN_PRICE_EN =
    /^Service price cannot be less than minimum allowed price\s*\(([0-9][0-9.,]*)\)\s*\.?\s*$/i;

function formatServiceMinPriceErrorMessage(message: string | undefined | null, t: TFunction): string {
    if (message == null || message === '') return '';
    const match = message.trim().match(SERVICE_MIN_PRICE_EN);
    if (match) return t('servicePriceBelowMinimumAllowed', { minPrice: match[1] });
    return message;
}

export default function ServicesPricelistListing() {
    const params = useParams();
    const serviceId = params?.id;
    const navigate = useNavigate();
    const [serviceTitle, setServiceTitle] = useState('');
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [loading, setLoading] = useState<boolean>(true);
    const [search, setSearch] = useState<any>('');
    const [searchTerm, setSearchTerm] = useState<any>('');
    const [service, setService] = useState<ServiceResponse>({});
    const { register, control, formState: { errors }, watch, setValue, trigger, reset } = useForm<FormData>({});
    const { data: tableData, isFetching, refetch } = searchTerm ? useGetServicePriceListService({ pageNumber: pageNo, size: pageSize, title: searchTerm }, +(params as any).id) : useGetServicePriceListService({ pageNumber: pageNo, size: pageSize }, +(params as any).id);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const loggedInUserData = useSelector(selectUserInfo);
    const defaultPriceListId = loggedInUserData?.business?.priceListId || 0;
    const columns = [
        { id: 'pricelistId', label: '', numeric: false, disablePadding: false },
        { id: 'title', label: t('title'), numeric: false },
        { id: 'price', label: t('price'), numeric: false },
    ];
    const fetchData = useCallback(() => {
        reset();
        refetch().then((res: any) => {
            if (!res?.data?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
        }).finally(() => setLoading(false));
    }, []);
    useEffect(() => {
        if(Object.keys(service)?.length > 0) {
            fetchData();
        }
    }, [pageNo, pageSize, searchTerm, service]);
    useEffect(() => {
        setTimeout(getByID, 100);
    }, []);
    const getByID = () => {
        ServiceProfileService.getServiceById(+(params as any)?.id).then((res: GetServiceByIdResponse) => {
            if (res.success) {
                setService(res?.data);
                const name = pickProfileEntityTitle(res);
                if (name) setServiceTitle(name);
            } else {
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err: any) => { console.error("===", err); });
    }
    const breadcrumbItems: BreadcrumbItem[] = useMemo(
        () => [
            { labelKey: 'Items' },
            { labelKey: 'services', href: '/services' },
            {
                labelKey: 'title',
                ...(serviceTitle ? { label: serviceTitle } : {}),
                href: serviceId ? `/service/${serviceId}` : undefined,
            },
            { labelKey: 'assignPriceList' },
        ],
        [serviceId, serviceTitle],
    );
    const clearSearch = () => {
        setSearch('');
        setSearchTerm('');
        if (searchTerm) { handleSearch(''); }
    }
    const handleSearch = (search: string) => {
        setSearchTerm(search);
        setPageNo(1);
        setPageSize(pageSize);
    };
    const handleSearchClick = () => {
        const trimmedSearch = search.trim();
        if (trimmedSearch !== '') handleSearch(trimmedSearch);
    };
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value.trim());
        if (!event.target.value.trim()) handleSearch('');
    };
    const assignUnassignSingleEntity = (isChecked: boolean, row: any) => {
        setLoading(true);
        if (isChecked) {
            onUnassignPriceList({ serviceId: +(params as any).id, pricelistId: row.id });
        } else {
            onAssignPriceList({ serviceId: +(params as any).id, pricelistId: row.id, price: row.price });
        }
    }
    const { onAssignPriceList } = useAssignServicePriceListService({
        onSuccess: (data: AssignUnassignResponse) => {
            if (data.success) {
                refetch();
                reset();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={data.status} text={t('priceList') + ' ' + t('assigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(
                    <AlertMessages
                        statusCode={data.status}
                        text={formatServiceMinPriceErrorMessage(data.message, t) || data.message}
                    />,
                    { variant: 'error' }
                );
            }
        },
        onError: (err: any) => {
            setLoading(false);
            const raw = err?.data?.message || err?.message || 'Something went wrong';
            const message = formatServiceMinPriceErrorMessage(raw, t) || raw;
            enqueueSnackbar(
                <AlertMessages statusCode={err?.status} text={message} />,
                { variant: 'error' }
            );
            if (err instanceof Error) {
                console.error(err.stack);
            }
        }
    })
    const { onUnassignPriceList } = useUnassignServicePriceListService({
        onSuccess: (data: AssignUnassignResponse) => {
            if (data.success) {
                refetch();
                reset();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={data.status} text={t('priceList') + ' ' + t('unassigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(
                    <AlertMessages
                        statusCode={data.status}
                        text={formatServiceMinPriceErrorMessage(data.message, t) || data.message}
                    />,
                    { variant: 'error' }
                );
            }
        },
        onError: (err: any) => {
            console.log("err====,  ", err);
            setLoading(false);
            const raw = err?.data?.message || err?.message || 'Something went wrong';
            const message = formatServiceMinPriceErrorMessage(raw, t) || raw;
            enqueueSnackbar(
                <AlertMessages statusCode={err?.status} text={message} />,
                { variant: 'error' }
            );
        },
    });

    return (
        <Box>
            <ComponentHeaderTwo
                showBtn={true}
                showSaveBtn={false}
                onClick={() => navigate('/services')}
                heading={t('services')}
                icon={<Services height={28} width={28} />}
                btnText={"back"}
                btnType={"back"}
                showCreateBtn={false}
                breadcrumbItems={breadcrumbItems}
            />
            <Box>
                <div className='flex'>
                    {params?.id ? <ServiceTabs params={params} selectedIndex={5} formData={service} /> : null}
                    <Box className='w-[100%]'>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={7} lg={5} className='mt-3'>
                                <StyledInput
                                    fullWidth
                                    placeholder={t("title")}
                                    handleSearchClick={handleSearchClick}
                                    handleChange={handleInputChange}
                                    value={search}
                                    clearSearch={clearSearch}
                                />
                            </Grid>
                        </Grid>
                        {(isFetching || loading) ? <Loader /> : <ServicesPricelistTable
                            columns={columns}
                            rows={tableData?.pages[0]?.data?.content || []}
                            assignUnassignSingleEntity={assignUnassignSingleEntity}
                            form={{ register, control, formState: { errors }, watch, setValue, trigger, reset }}
                            assignPrivilege={checkPrivileges('ROLE_SERVICE_PRICELIST_ASSIGN')}
                            unassignPrivilege={checkPrivileges('ROLE_SERVICE_PRICELIST_UNASSIGN')}
                            {...{pageNo, pageSize, defaultPriceListId}}
                        />}
                    </Box>
                </div>
            </Box>
        </Box>
    )
}